import { saveAs } from 'file-saver'
import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'

// UI
import { Box, Button, Flex, HStack, Heading, Spacer } from '@chakra-ui/react'
import { toast } from 'react-toastify'
import GCKCodeDetailModal from '../components/GCKCodeDetailModal'

import { NonFormUpload } from '@/components/Elements'

//Hooks
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//Helper
import { createExcelBlob } from '@/utils/fileHelper'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import getGCKCodeTableColumns from '../constants/GCKCodeTableColumns'
import { useGCKCode } from '../useGCKCode'
import { LoadingSection } from '@/components/Elements'

const GCKCodeListPage = () => {
  //Modal State
  const [modalDetail, setModalDetail] = useState({
    show: false,
    isCreate: true,
  })

  // Table State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:GCKCode`, {})

  const query = convertTableStateToQuery(storedTableState)

  const { useFetchAllCostCenters, useCostCenterOptions } = useCostCenter()
  const { useFetchAllGCKCodes, useRemoveGCKCode, useUpdateGCKCode, useUploadGCKCodes } = useGCKCode()

  const { data: GCKCodeResponse, isLoading } = useFetchAllGCKCodes(query)
  const { data: allGCKCodeResponse } = useFetchAllGCKCodes({
    page: 1,
    pageSize: 0,
  }) // Page Size 0 means get all

  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()
  const uploadGCKCodes = useUploadGCKCodes()
  const removeGCKCode = useRemoveGCKCode()
  const updateGCKCode = useUpdateGCKCode()

  const { data: costCenterOptions } = useCostCenterOptions({ labelType: 'simple' })

  const handleOnRowClick = (row) => {
    setModalDetail({
      show: true,
      isCreate: false,
      detail: row?.original,
    })
  }
  const handleCreateGCKCode = () => {
    setModalDetail({
      show: true,
      isCreate: true,
      detail: {},
    })
  }

  const handleUpload = (file) => {
    if (!file) {
      toast.error('No File Uploaded')
      return
    }
    dispatch(showLoadingOverlay())
    uploadGCKCodes.mutate(file, {
      onSettled: () => {
        dispatch(hideLoadingOverlay())
      },
    })
  }

  const handleDownloadAll = useCallback(() => {
    if (!allGCKCodeResponse?.data) {
      return
    }
    try {
      const excelData = allGCKCodeResponse.data.map((row) => ({
        profitCenter: row.profitCenter,
        description: row.description,
        GCK: row.GCK,
        GCKDescription: row.GCKDescription,
        status: row.status,
      }))
      const excelBlob = createExcelBlob(excelData)
      saveAs(excelBlob, `GCK Code.xlsx`)
    } catch (err) {
      console.log(err)
    }
  }, [allGCKCodeResponse?.data])

  const handleDelete = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())

        await removeGCKCode.mutateAsync(id)
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, removeGCKCode]
  )

  const handleActivate = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())

        await updateGCKCode.mutateAsync({ id, data: { isDeleted: false } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, updateGCKCode]
  )

  const defaultColumns = getGCKCodeTableColumns({
    costCenterOptions,
    handleActivate,
    handleDelete,
  })

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <>
      <GCKCodeDetailModal modalDetail={modalDetail} setModalDetail={setModalDetail} />
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading>GCK Codes</Heading>
          <HStack mx="5">
            <Button colorScheme="gray" onClick={handleDownloadAll}>
              Download
            </Button>
            <NonFormUpload uploadHandler={handleUpload} />
          </HStack>
          <Spacer />
          <Button colorScheme="green" onClick={handleCreateGCKCode}>
            Add GCKCode
          </Button>
        </Flex>
        <Box p={5} overflow="scroll">
          <FullTable
            data={GCKCodeResponse?.data ? GCKCodeResponse.data : []}
            rowCount={GCKCodeResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleOnRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default GCKCodeListPage
